import { VegaThemeManager } from '@globalpayments/vega';
import { useEffect, useLayoutEffect, useState } from 'react';

import { ColorThemePreferenceKey } from '../types/localStorage';

export type ColorTheme = 'dark' | 'light';

const colorSchemeQuery = '(prefers-color-scheme: dark)';

const isColorTheme = (value: string | null): value is ColorTheme => value === 'dark' || value === 'light';

const getSystemTheme = (): ColorTheme =>
  typeof window.matchMedia === 'function' && window.matchMedia(colorSchemeQuery).matches ? 'dark' : 'light';

const getInitialTheme = (): ColorTheme => {
  const storedTheme = localStorage.getItem(ColorThemePreferenceKey);
  return isColorTheme(storedTheme) ? storedTheme : getSystemTheme();
};

/** Applies Vega color themes and persists explicit user preferences. */
export const useColorTheme = () => {
  const [colorTheme, setColorTheme] = useState<ColorTheme>(getInitialTheme);

  useLayoutEffect(() => {
    VegaThemeManager.toggleDarkMode(colorTheme === 'dark');
    document.documentElement.style.colorScheme = colorTheme;
  }, [colorTheme]);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;

    const mediaQuery = window.matchMedia(colorSchemeQuery);
    const handleSystemThemeChange = (event: MediaQueryListEvent) => {
      if (localStorage.getItem(ColorThemePreferenceKey) === null) {
        setColorTheme(event.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
  }, []);

  const toggleColorTheme = () => {
    setColorTheme((currentTheme) => {
      const nextTheme = currentTheme === 'dark' ? 'light' : 'dark';
      localStorage.setItem(ColorThemePreferenceKey, nextTheme);
      return nextTheme;
    });
  };

  return { colorTheme, toggleColorTheme };
};